/**
 * Global Price Replacer — uses sync-first country resolution
 * Does NOT affect cart or checkout pages.
 */
import {
  resolveCountryCodeSync,
  resolveCountryCodeAsync,
  formatPriceForCountry,
  CountryCode,
} from './priceConfig'

const PRICE_SELECTORS = [
  '.price',
  '.price-item',
  '.price__regular',
  '.price__sale',
  '.product__price',
  '.product-price',
  '.money',
  'span.money',
  '[data-price]',
  '[data-money]',
  '[data-product-price]',
  '[data-price-usd]',
  '.compare-at',
  '.compare-at-price',
  '.price__compare',
  '.was-price',
  '.original-price',
  '[data-compare-at-usd]',
  '.product__price-amount',
  '.price-amount',
  '[data-price-amount]',
]

const EXCLUDED_PATHS = ['/cart', '/checkout', '/checkouts']

function isExcludedPage(): boolean {
  return EXCLUDED_PATHS.some((p) =>
    window.location.pathname.toLowerCase().includes(p),
  )
}

function isCompareAt(el: HTMLElement): boolean {
  const cl = el.className.toLowerCase()
  return (
    cl.includes('compare') ||
    cl.includes('was-price') ||
    cl.includes('original') ||
    cl.includes('line-through') ||
    'compareAtUsd' in el.dataset ||
    el.style.textDecoration === 'line-through'
  )
}

function extractUsd(text: string): number | null {
  const cleaned = text.replace(/[^\d.,]/g, '').trim()
  if (!cleaned) return null
  const n = parseFloat(cleaned.replace(',', '.'))
  return isNaN(n) ? null : n
}

function applyReplacements(cc: CountryCode): void {
  const els = document.querySelectorAll(PRICE_SELECTORS.join(', '))
  els.forEach((el) => {
    if (!(el instanceof HTMLElement) || el.dataset.priceReplaced === 'true')
      return
    const text = el.textContent?.trim() || ''
    if (!text) return

    let usd: number | null = null
    if (el.dataset.priceUsd) usd = parseFloat(el.dataset.priceUsd)
    else if (el.dataset.compareAtUsd) usd = parseFloat(el.dataset.compareAtUsd)
    else usd = extractUsd(text)

    if (!usd || usd <= 0) return

    try {
      el.textContent = formatPriceForCountry(
        usd,
        cc,
        isCompareAt(el) ? 'compareAt' : 'base',
      )
      el.dataset.priceReplaced = 'true'
    } catch {}
  })
}

export async function initGlobalPriceReplacer(): Promise<void> {
  if (isExcludedPage()) return

  // Try sync first (instant)
  const sync = resolveCountryCodeSync()
  let cc: CountryCode

  if (sync) {
    cc = sync.code
  } else {
    const async = await resolveCountryCodeAsync()
    cc = async.code
  }

  applyReplacements(cc)

  // Watch for dynamically added price elements
  const obs = new MutationObserver(() => {
    setTimeout(() => applyReplacements(cc), 100)
  })
  obs.observe(document.body, { childList: true, subtree: true })
}
